import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { bookingService } from '../services/bookingService';
import './EquipmentPage.css';

const EquipmentPage = ({ 
  user = null, 
  addNotification = () => {}, 
  addAuditLog = () => {},
  generateBookingId = () => `RA-${Date.now()}`
}) => {
  const [equipmentRequest, setEquipmentRequest] = useState({
    items: [],
    purpose: '',
    borrowDuration: '24',
    notes: '',
    sportingActivity: false,
    phone: user?.phone || '',
    whatsapp: user?.whatsApp || ''
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // States for equipment data from SQL
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  const [equipmentByCategory, setEquipmentByCategory] = useState({});
  const [categories, setCategories] = useState(['all']);
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  const [equipmentError, setEquipmentError] = useState(null);
  
  // States for dependent approval
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [approvalFile, setApprovalFile] = useState(null);
  const [approvalFileName, setApprovalFileName] = useState('');

  // PUBLIC function to fetch equipment from SQL (NO AUTH REQUIRED)
  const fetchPublicEquipment = async () => {
    const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
    
    setLoadingEquipment(true);
    setEquipmentError(null);
    
    try {
      console.log('📡 EquipmentPage: Fetching equipment from SQL via PUBLIC endpoint...');
      
      // PUBLIC endpoint - NO Authorization header needed!
      const response = await fetch(`${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
          // IMPORTANT: Do NOT include Authorization header here
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Equipment API Response:', data);
      
      if (data.success && data.equipment) {
        const equipment = data.equipment;
        setEquipmentCatalog(equipment);
        
        // Group equipment by category
        const grouped = equipment.reduce((acc, item) => {
          const category = item.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {});
        
        setEquipmentByCategory(grouped);
        
        // Get unique categories for filter
        const uniqueCategories = ['all', ...Object.keys(grouped)];
        setCategories(uniqueCategories);
        
        console.log(`✅ Loaded ${equipment.length} equipment items from SQL`);
        console.log('📁 Categories:', Object.keys(grouped));
      } else {
        console.warn('⚠️ No equipment data received from API:', data);
        setEquipmentError(data.error || 'No equipment data received');
        setEquipmentCatalog([]);
        setEquipmentByCategory({});
      }
    } catch (error) {
      console.error('❌ Error fetching equipment from SQL:', error);
      setEquipmentError(error.message);
      addNotification('Failed to load equipment data: ' + error.message, 'error');
      setEquipmentCatalog([]);
      setEquipmentByCategory({});
    } finally {
      setLoadingEquipment(false);
    }
  };

  // Fetch equipment from SQL database using public endpoint
  useEffect(() => {
    fetchPublicEquipment();
  }, []);

  // Check if user is dependent and needs approval
  useEffect(() => {
    if (user) {
      // Full members with full_member category - NO approval needed
      const isFullMember = user.membership_type === 'primary' && user.membership_category === 'full_member';
      
      // Spouse of full member - NO approval needed
      const isSpouse = user.membership_type === 'dependent' && user.relationship_type === 'spouse';
      
      // All other dependents need approval
      const needsApproval = user.membership_type === 'dependent' && !isSpouse;
      
      setRequiresApproval(needsApproval);
      
      if (needsApproval) {
        console.log('⚠️ Dependent member detected - approval file required for equipment borrowing');
      }
    }
  }, [user]);

  // Filter equipment by category and search
  const getFilteredEquipment = () => {
    let items = [];
    
    if (selectedCategory === 'all') {
      Object.values(equipmentByCategory || {}).forEach(catItems => {
        if (Array.isArray(catItems)) {
          items = [...items, ...catItems];
        }
      });
    } else {
      items = equipmentByCategory[selectedCategory] || [];
    }

    // Apply sporting activity filter
    if (equipmentRequest.sportingActivity) {
      items = items.filter(item => item?.sporting_only === 1 || item?.sportingOnly === true || item?.sportingOnly === 1);
    }

    // Apply search filter
    if (searchTerm) {
      items = items.filter(item => 
        item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return items;
  };

  const handleEquipmentSubmit = async () => {
    if (equipmentRequest.items.length === 0) {
      addNotification('Please select at least one item', 'error');
      return;
    }

    if (!equipmentRequest.purpose) {
      addNotification('Please enter a purpose for borrowing', 'error');
      return;
    }

    if (!equipmentRequest.phone && !user?.phone) {
      addNotification('Please enter your phone number', 'error');
      return;
    }

    // Validate approval file for dependent members
    if (requiresApproval && !approvalFile) {
      addNotification('⚠️ Please upload an approval document from your primary member to borrow equipment.', 'error');
      return;
    }

    setLoading(true);

    try {
      // Prepare equipment items for API
      const equipment_items = equipmentRequest.items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity || 1,
        category: item.category
      }));

      let response;

      if (requiresApproval && approvalFile) {
        // Use FormData for dependent booking with file upload
        const formData = new FormData();
        formData.append('booking_type', 'equipment');
        formData.append('purpose', equipmentRequest.purpose);
        formData.append('borrow_duration', equipmentRequest.borrowDuration);
        formData.append('equipment_items', JSON.stringify(equipment_items));
        formData.append('notes', equipmentRequest.notes);
        formData.append('contact_phone', equipmentRequest.phone || user?.phone);
        formData.append('contact_whatsapp', equipmentRequest.whatsapp || user?.whatsApp);
        formData.append('contact_email', user?.email);
        formData.append('approval_file', approvalFile);
        
        const token = localStorage.getItem('nlngra_token');
        const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
        
        const fetchResponse = await fetch(`${API_BASE_URL}/bookings.php?action=create`, {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token
          },
          body: formData
        });
        
        const rawText = await fetchResponse.text();
        console.log('📡 Raw response:', rawText);
        
        try {
          response = JSON.parse(rawText);
        } catch (e) {
          const jsonMatch = rawText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            response = JSON.parse(jsonMatch[0]);
          } else {
            throw new Error('Invalid response from server');
          }
        }
      } else {
        // Use regular JSON for non-dependent bookings
        const bookingPayload = {
          booking_type: 'equipment',
          purpose: equipmentRequest.purpose,
          borrow_duration: equipmentRequest.borrowDuration,
          equipment_items: equipment_items,
          notes: equipmentRequest.notes,
          contact_phone: equipmentRequest.phone || user?.phone,
          contact_whatsapp: equipmentRequest.whatsapp || user?.whatsApp,
          contact_email: user?.email
        };

        console.log('📡 Submitting equipment request:', bookingPayload);
        response = await bookingService.createBooking(bookingPayload);
      }

      console.log('📡 API Response:', response);

      if (response && response.success) {
        const bookingNumber = response.booking?.booking_number || response.booking_number || 'N/A';
        
        if (response.requires_approval) {
          addNotification(
            `📄 Equipment request #${bookingNumber} submitted with approval document. Awaiting admin review. Items must be returned within ${equipmentRequest.borrowDuration} hours.`,
            'info'
          );
        } else {
          addNotification(
            `✅ Equipment request #${bookingNumber} submitted! Items must be returned within ${equipmentRequest.borrowDuration} hours.`,
            'success'
          );
        }
        
        if (addAuditLog) {
          addAuditLog('Equipment Request Submitted', { 
            requestId: bookingNumber, 
            items: equipmentRequest.items.length,
            purpose: equipmentRequest.purpose,
            requiresApproval: requiresApproval
          });
        }
        
        // Reset form
        setEquipmentRequest({
          items: [],
          purpose: '',
          borrowDuration: '24',
          notes: '',
          sportingActivity: false,
          phone: user?.phone || '',
          whatsapp: user?.whatsApp || ''
        });
        setApprovalFile(null);
        setApprovalFileName('');
        
      } else {
        addNotification(response?.message || 'Failed to submit equipment request', 'error');
      }
    } catch (error) {
      console.error('Equipment request error:', error);
      addNotification('An error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleEquipmentItem = (equipment) => {
    const exists = equipmentRequest.items.find(e => e.id === equipment.id);
    if (exists) {
      setEquipmentRequest({
        ...equipmentRequest,
        items: equipmentRequest.items.filter(e => e.id !== equipment.id)
      });
    } else {
      setEquipmentRequest({
        ...equipmentRequest,
        items: [...equipmentRequest.items, { ...equipment, quantity: 1 }]
      });
    }
  };

  const updateEquipmentItemQuantity = (equipmentId, quantity) => {
    setEquipmentRequest({
      ...equipmentRequest,
      items: equipmentRequest.items.map(e => 
        e.id === equipmentId ? { ...e, quantity: Math.max(1, quantity) } : e
      )
    });
  };

  const removeEquipmentItem = (equipmentId) => {
    setEquipmentRequest({
      ...equipmentRequest,
      items: equipmentRequest.items.filter(e => e.id !== equipmentId)
    });
  };

  const getTotalItems = () => {
    return equipmentRequest.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  };

  const filteredItems = getFilteredEquipment();

  // Show loading state
  if (loadingEquipment) {
    return (
      <div className="equipment-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading equipment from database...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (equipmentError) {
    return (
      <div className="equipment-page">
        <div className="error-container">
          <i className="fa-solid fa-exclamation-triangle"></i>
          <h3>Unable to load equipment</h3>
          <p>{equipmentError}</p>
          <button 
            className="retry-btn"
            onClick={fetchPublicEquipment}
          >
            <i className="fa-solid fa-rotate-right"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  const hasEquipment = Object.keys(equipmentByCategory || {}).length > 0;

  return (
    <div className="equipment-page">
      <div className="page-header">
        <h2 className="page-title">
          <i className="fa-solid fa-toolbox"></i>
          Borrow Equipment
        </h2>
        
        {/* Dependent Warning Banner */}
        {requiresApproval && (
          <div className="dependent-warning-banner">
            <i className="fa-solid fa-file-upload"></i>
            <div className="warning-content">
              <strong>Dependent Member Notice:</strong>
              <p>As a dependent member, you need to upload an approval document from your primary member for each equipment request.</p>
            </div>
          </div>
        )}
      </div>

      {/* Request Form */}
      <div className="request-form-card">
        <h3 className="form-title">
          <i className="fa-solid fa-circle-info"></i>
          Request Details
        </h3>

        <div className="form-group">
          <label>
            <i className="fa-solid fa-clipboard-list"></i>
            Purpose *
          </label>
          <input
            type="text"
            placeholder="e.g., Tennis practice, Birthday party"
            value={equipmentRequest.purpose}
            onChange={(e) => setEquipmentRequest({...equipmentRequest, purpose: e.target.value})}
            disabled={loading}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <i className="fa-regular fa-clock"></i>
              Borrow Duration *
            </label>
            <select
              value={equipmentRequest.borrowDuration}
              onChange={(e) => setEquipmentRequest({...equipmentRequest, borrowDuration: e.target.value})}
              disabled={loading}
            >
              <option value="1">1 hour</option>
              <option value="2">2 hours</option>
              <option value="3">3 hours</option>
              <option value="4">4 hours</option>
              <option value="6">6 hours</option>
              <option value="8">8 hours</option>
              <option value="12">12 hours</option>
              <option value="24">24 hours (max)</option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={equipmentRequest.sportingActivity}
                onChange={(e) => {
                  const isSporting = e.target.checked;
                  setEquipmentRequest({
                    ...equipmentRequest, 
                    sportingActivity: isSporting,
                    items: isSporting 
                      ? equipmentRequest.items.filter(item => item?.sporting_only === 1 || item?.sportingOnly === true)
                      : equipmentRequest.items
                  });
                }}
                disabled={loading}
              />
              <span>Sporting Activity Only</span>
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-phone"></i>
              Phone Number *
            </label>
            <input
              type="tel"
              placeholder="For SMS notifications"
              value={equipmentRequest.phone || user?.phone || ''}
              onChange={(e) => setEquipmentRequest({...equipmentRequest, phone: e.target.value})}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-brands fa-whatsapp"></i>
              WhatsApp Number
            </label>
            <input
              type="tel"
              placeholder="For WhatsApp notifications"
              value={equipmentRequest.whatsapp || user?.whatsApp || ''}
              onChange={(e) => setEquipmentRequest({...equipmentRequest, whatsapp: e.target.value})}
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label>
            <i className="fa-solid fa-note-sticky"></i>
            Additional Notes
          </label>
          <textarea
            rows="2"
            placeholder="Any special requirements?"
            value={equipmentRequest.notes}
            onChange={(e) => setEquipmentRequest({...equipmentRequest, notes: e.target.value})}
            disabled={loading}
          />
        </div>

        {/* Dependent Approval File Upload Section */}
        {requiresApproval && (
          <div className="dependent-approval-section">
            <h4 className="section-title">
              <span className="title-icon">📄</span>
              Dependent Approval Document <span className="required-star">*</span>
            </h4>
            <div className="approval-upload-area">
              <div className="upload-instructions">
                <i className="fa-solid fa-info-circle"></i>
                <p>Upload an approval document from your primary member (parent/sponsor) to borrow equipment.</p>
              </div>
              
              <div className="file-upload-container">
                <label className={`file-upload-label ${approvalFileName ? 'has-file' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setApprovalFile(file);
                        setApprovalFileName(file.name);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-button">
                    <i className="fa-solid fa-cloud-upload-alt"></i>
                    <span>{approvalFileName || 'Click to upload approval document'}</span>
                  </div>
                </label>
                <p className="upload-hint">
                  <i className="fa-regular fa-circle-check"></i>
                  Accepted formats: PDF, JPG, PNG (Max 5MB)
                </p>
              </div>
              
              {approvalFile && (
                <div className="uploaded-file-info">
                  <i className="fa-solid fa-check-circle"></i>
                  <span className="file-name">{approvalFileName}</span>
                  <button 
                    type="button"
                    className="remove-file-btn"
                    onClick={() => {
                      setApprovalFile(null);
                      setApprovalFileName('');
                    }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Equipment Selection */}
      <div className="equipment-selection-card">
        <div className="selection-header">
          <h3 className="form-title">
            <i className="fa-solid fa-kitchen-set"></i>
            Select Equipment
          </h3>
          
          <div className="selected-count">
            <i className="fa-solid fa-check-circle"></i>
            <span>{getTotalItems()} item{getTotalItems() !== 1 ? 's' : ''} selected</span>
            {equipmentRequest.items.length > 0 && (
              <button 
                className="clear-btn"
                onClick={() => setEquipmentRequest({...equipmentRequest, items: []})}
                disabled={loading}
              >
                <i className="fa-solid fa-times"></i> Clear
              </button>
            )}
          </div>
        </div>

        {/* Search and Filter */}
        <div className="search-filter">
          <div className="search-bar">
            <i className="fa-solid fa-search"></i>
            <input
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="category-filters">
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
              disabled={loading}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Selected Items Preview */}
        {equipmentRequest.items.length > 0 && (
          <div className="selected-items-preview">
            <h4>Selected Items:</h4>
            <div className="preview-list">
              {equipmentRequest.items.map(item => (
                <div key={item.id} className="preview-item">
                  <i className={item.icon || 'fa-solid fa-toolbox'}></i>
                  <span className="item-name">{item.name}</span>
                  <div className="item-quantity">
                    <button 
                      onClick={() => updateEquipmentItemQuantity(item.id, (item.quantity || 1) - 1)}
                      disabled={loading || (item.quantity || 1) <= 1}
                    >
                      <i className="fa-solid fa-minus"></i>
                    </button>
                    <span>{item.quantity || 1}</span>
                    <button 
                      onClick={() => updateEquipmentItemQuantity(item.id, (item.quantity || 1) + 1)}
                      disabled={loading || (item.quantity || 1) >= (item.available || 99)}
                    >
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </div>
                  <button 
                    className="remove-item"
                    onClick={() => removeEquipmentItem(item.id)}
                    disabled={loading}
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equipment Grid - Data from SQL */}
        <div className="equipment-grid-container">
          {!hasEquipment ? (
            <div className="empty-equipment">
              <i className="fa-solid fa-box-open"></i>
              <p>No equipment available in the database.</p>
              <p className="empty-hint">Please check back later or contact the administrator.</p>
              <button 
                className="retry-btn"
                onClick={fetchPublicEquipment}
              >
                <i className="fa-solid fa-rotate-right"></i>
                Refresh Equipment
              </button>
            </div>
          ) : (
            Object.entries(equipmentByCategory).map(([category, items]) => {
              const filteredCategoryItems = (items || []).filter(item => {
                if (equipmentRequest.sportingActivity && !(item?.sporting_only === 1 || item?.sportingOnly === true)) return false;
                if (searchTerm && !item?.name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                if (selectedCategory !== 'all' && category !== selectedCategory) return false;
                return true;
              });

              if (filteredCategoryItems.length === 0) return null;

              return (
                <div key={category} className="equipment-category">
                  <h4 className="category-title">
                    {category}
                    <span className="item-count">{filteredCategoryItems.length} items</span>
                  </h4>
                  <div className="equipment-grid">
                    {filteredCategoryItems.map(item => {
                      const isSelected = equipmentRequest.items.some(e => e.id === item.id);
                      const isSporting = item?.sporting_only === 1 || item?.sportingOnly === true;
                      return (
                        <motion.button
                          key={item.id}
                          className={`equipment-card ${isSelected ? 'selected' : ''} ${isSporting ? 'sporting' : ''}`}
                          onClick={() => toggleEquipmentItem(item)}
                          whileTap={{ scale: 0.95 }}
                          disabled={loading}
                        >
                          <i className={item.icon || 'fa-solid fa-toolbox'}></i>
                          <span className="equipment-name">{item.name}</span>
                          <span className="equipment-available">
                            {item.available || 0} available
                          </span>
                          {isSporting && (
                            <span className="sporting-badge">
                              <i className="fa-solid fa-medal"></i>
                            </span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="submit-section">
        <button 
          className="submit-btn"
          onClick={handleEquipmentSubmit}
          disabled={equipmentRequest.items.length === 0 || !equipmentRequest.purpose || loading || (requiresApproval && !approvalFile)}
        >
          {loading ? (
            <>
              <i className="fa-solid fa-spinner fa-spin"></i>
              Submitting...
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane"></i>
              {requiresApproval ? 'Submit with Approval Document' : 'Submit Borrow Request'} ({getTotalItems()} items)
            </>
          )}
        </button>
        
        <p className="terms-note">
          <i className="fa-solid fa-circle-info"></i>
          By submitting, you agree to return all items within {equipmentRequest.borrowDuration} hours
        </p>
      </div>
    </div>
  );
};

export default EquipmentPage;